(() => {
  if (customElements.get('modal-element')) return;

  const openModals = new Set();

  function syncBodyLock() {
    document.body.classList.toggle('has-modal-open', openModals.size > 0);
  }

  function toMilliseconds(value) {
    const duration = Number.parseFloat(value);
    return value.trim().endsWith('ms') ? duration : duration * 1000;
  }

  function getTransitionDuration(element) {
    if (!element) return 0;

    const styles = window.getComputedStyle(element);
    const durations = styles.transitionDuration.split(',').map(toMilliseconds);
    const delays = styles.transitionDelay.split(',').map(toMilliseconds);
    const count = Math.max(durations.length, delays.length);

    return Array.from({ length: count }, (_, index) => {
      return durations[index % durations.length] + delays[index % delays.length];
    }).reduce((maximum, duration) => Math.max(maximum, duration), 0);
  }

  function waitForTransition(element) {
    const duration = getTransitionDuration(element);
    if (duration === 0) return Promise.resolve();

    return new Promise((resolve) => {
      const finish = () => {
        element.removeEventListener('transitionend', onTransitionEnd);
        window.clearTimeout(timeout);
        resolve();
      };
      const onTransitionEnd = (event) => {
        if (event.target === element) finish();
      };
      const timeout = window.setTimeout(finish, duration + 50);

      element.addEventListener('transitionend', onTransitionEnd);
    });
  }

  class ModalElement extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.transitionToken = 0;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };

      document.addEventListener('click', (event) => this.onDocumentClick(event), options);
      document.addEventListener('keydown', (event) => this.onKeydown(event), options);
      document.addEventListener('modal:closeAll', () => this.hide(), options);
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', (event) => this.onSectionSelect(event), options);
        document.addEventListener('shopify:section:deselect', (event) => this.onSectionDeselect(event), options);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      openModals.delete(this);
      syncBodyLock();
      this.initialized = false;
    }

    get open() {
      return this.hasAttribute('open');
    }

    get controls() {
      if (!this.id) return [];
      return [...document.querySelectorAll(`[aria-controls="${CSS.escape(this.id)}"]`)];
    }

    get dialog() {
      return this.querySelector('[data-modal-dialog]') || this.querySelector('[role="document"]') || this;
    }

    get focusElement() {
      return this.querySelector('[data-modal-autofocus]')
        || this.querySelector('[data-modal-close]')
        || this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
        || this.dialog;
    }

    onDocumentClick(event) {
      if (!this.id || !(event.target instanceof Element)) return;

      const control = event.target.closest(`[aria-controls="${CSS.escape(this.id)}"]`);
      if (!control) return;

      const shouldClose = control.hasAttribute('data-modal-close') || this.contains(control);

      event.preventDefault();
      if (shouldClose || this.open) {
        this.hide();
      } else {
        this.show(control);
      }
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.open) return;

      const topModal = [...openModals].at(-1);
      if (topModal !== this) return;

      event.preventDefault();
      this.hide();
    }

    onSectionSelect(event) {
      if (event.target?.contains(this)) this.show();
    }

    onSectionDeselect(event) {
      if (event.target?.contains(this)) this.hide();
    }

    show(opener = null) {
      if (this.open) return;

      this.transitionToken += 1;
      this.openedBy = opener instanceof HTMLElement ? opener : document.activeElement;
      this.hidden = false;
      this.removeAttribute('inert');
      this.setAttribute('open', '');
      this.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        image.loading = 'eager';
      });
      this.setTriggerState(true);
      openModals.add(this);
      syncBodyLock();
      this.dispatchEvent(new CustomEvent('modal:beforeShow', { bubbles: true }));

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.open) return;

          this.setAttribute('active', '');
          if (typeof trapFocus === 'function') {
            trapFocus(this, this.focusElement);
          } else {
            this.focusElement?.focus();
          }
          this.dispatchEvent(new CustomEvent('modal:afterShow', { bubbles: true }));
        });
      });
    }

    async hide() {
      if (!this.open) return;

      const transitionToken = ++this.transitionToken;
      this.dispatchEvent(new CustomEvent('modal:beforeHide', { bubbles: true }));
      this.removeAttribute('active');
      this.removeAttribute('open');
      this.setTriggerState(false);
      openModals.delete(this);
      syncBodyLock();

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.openedBy);
      } else {
        this.openedBy?.focus();
      }
      this.setAttribute('inert', '');

      await waitForTransition(this);
      if (transitionToken !== this.transitionToken || this.open) return;

      this.hidden = true;
      this.dispatchEvent(new CustomEvent('modal:afterHide', { bubbles: true }));
    }

    setTriggerState(expanded) {
      this.controls
        .filter((control) => !this.contains(control) && !control.hasAttribute('data-modal-close'))
        .forEach((control) => control.setAttribute('aria-expanded', String(expanded)));
    }
  }

  customElements.define('modal-element', ModalElement);
})();
